import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";
import { validateCoupon } from "../services/couponService";

const CouponContext = createContext(null);

export const useCoupon = () => {
  const context = useContext(CouponContext);
  if (!context) {
    throw new Error("useCoupon must be used within CouponProvider");
  }
  return context;
};

export const CouponProvider = ({ children }) => {
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  /**
   * Apply a coupon to the cart
   */
  const applyCoupon = useCallback(async (couponCode, cartTotal, customerId = null, items = null, customerEmail = null) => {
    if (!couponCode || couponCode.trim() === "") {
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setError("");
      setMessage("");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const result = await validateCoupon(couponCode, cartTotal, customerId, items, customerEmail);

      if (result.valid) {
        setAppliedCoupon({
          code: couponCode,
          id: result.coupon_id,
          data: result.coupon_data,
        });
        setDiscountAmount(result.discount_amount);
        setMessage(result.message || "Coupon applied successfully!");
        toast.success(result.message || "Coupon applied successfully!");
        return { success: true, discount: result.discount_amount, message: result.message };
      } else {
        setError(result.message || "Invalid coupon code");
        setAppliedCoupon(null);
        setDiscountAmount(0);
        toast.error(result.message || "Invalid coupon code");
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || err.message || "Failed to apply coupon";
      setError(errorMsg);
      setAppliedCoupon(null);
      setDiscountAmount(0);
      toast.error(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Remove the applied coupon
   */
  const removeCoupon = useCallback((silent = false) => {
    if (appliedCoupon && !silent) toast.info("Coupon removed");
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setError("");
    setMessage("");
  }, [appliedCoupon]);

  const revalidateCoupon = useCallback(async (couponCode, cartTotal, customerId = null, items = null, customerEmail = null) => {
    if (!couponCode) return { success: false, discount: 0 };
    try {
      const result = await validateCoupon(couponCode, cartTotal, customerId, items, customerEmail);
      if (!result.valid) {
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setError(result.message || "This coupon no longer applies to your cart.");
        setMessage("");
        return { success: false, discount: 0, message: result.message };
      }
      setAppliedCoupon({ code: couponCode, id: result.coupon_id, data: result.coupon_data });
      setDiscountAmount(result.discount_amount);
      setError("");
      return { success: true, discount: result.discount_amount, message: result.message };
    } catch {
      return { success: false, discount: 0 };
    }
  }, []);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setError("");
    setMessage("");
  }, []);

  const value = {
    appliedCoupon,
    discountAmount,
    loading,
    error,
    message,
    applyCoupon,
    revalidateCoupon,
    removeCoupon,
    clearMessages,
    isApplied: !!appliedCoupon,
  };

  return <CouponContext.Provider value={value}>{children}</CouponContext.Provider>;
};
